import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import SacredSpaceTab from '../../components/SacredSpaceTab';

const NUCLEO_IMAGE = 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1775736495/Esfera_dorada_en_templo_celestial_kzcvma.png';

const CRYSTAL_AMBER = 'https://static.prod-images.emergentagent.com/jobs/0d984e69-b9d3-4602-bf0f-4885fb6742e4/images/c02a8aada68022c605d4296316687f446daee794c8889368c8441d2f4b6d57e8.png';
const CRYSTAL_BLUE = 'https://static.prod-images.emergentagent.com/jobs/0d984e69-b9d3-4602-bf0f-4885fb6742e4/images/5e47bfea9e2eb652b495300646dc9215e273b340c96511b842fca56444d16f60.png';
const CRYSTAL_WHITE = 'https://static.prod-images.emergentagent.com/jobs/0d984e69-b9d3-4602-bf0f-4885fb6742e4/images/1c0fcbfd7ac93ab8d44c699651c6f3235a880076c65087e700e7b4415688b61e.png';

const PRACTICES = [
  {
    id: 'enraizamiento',
    name: 'Enraizamiento',
    rhythm: '4 · 7 · 8',
    crystal: CRYSTAL_AMBER,
    color: 'rgba(212,175,55,.7)',
    glowColor: 'rgba(212,175,55,.2)',
    phases: [
      { label: 'Inhala', duration: 4000 },
      { label: 'Retén', duration: 7000 },
      { label: 'Exhala', duration: 8000 },
    ],
    animName: 'breathEnraizamiento',
    animDur: '19s',
  },
  {
    id: 'coherencia',
    name: 'Coherencia',
    rhythm: '5 · 5',
    crystal: CRYSTAL_BLUE,
    color: 'rgba(160,195,230,.7)',
    glowColor: 'rgba(140,180,220,.2)',
    phases: [
      { label: 'Inhala', duration: 5000 },
      { label: 'Exhala', duration: 5000 },
    ],
    animName: 'breathCoherencia',
    animDur: '10s',
  },
  {
    id: 'oceanica',
    name: 'Oceánica',
    rhythm: 'fluida',
    crystal: CRYSTAL_WHITE,
    color: 'rgba(220,220,235,.65)',
    glowColor: 'rgba(200,200,220,.18)',
    phases: [
      { label: 'Inhala', duration: 6000 },
      { label: 'Exhala', duration: 6000 },
    ],
    animName: 'breathOceanica',
    animDur: '12s',
  },
];

const DECRETOS = [
  "Soy raíz y cielo.\nMi cuerpo es templo sagrado.",
  "Confío en el ritmo\nde mi propia transformación.",
  "Mi corazón late\nal compás de la Tierra.",
  "Soy la calma\nen el centro de todo.",
  "Mi respiración es\nel puente entre mundos.",
  "Honro el silencio\nque habita en mí.",
  "Soy agua, soy fuego,\nsoy la vida que fluye.",
  "Mi presencia es\nsuficiente y sagrada.",
  "En cada célula\nhabita la memoria del origen.",
  "Permito que la tierra\nme sostenga completamente.",
  "Mi cuerpo sabe.\nYo escucho.",
  "Soy el silencio fértil\ndonde todo germina.",
  "Cada latido\nes un decreto de vida.",
  "Me entrego al misterio\ncon los ojos abiertos.",
  "La tierra me recuerda\nquién soy realmente.",
  "Respiro con Gaia.\nSomos una.",
  "Soy presencia pura\nen este instante eterno.",
  "Mi cuerpo es el altar\ndonde todo se transforma.",
  "La quietud interior\nes mi verdadera fuerza.",
  "Soy raíz profunda\ny copa que toca el cielo.",
  "Me permito ser\nsin necesidad de hacer.",
  "El silencio habla.\nYo recibo.",
  "Cada respiración\nes un acto de creación.",
  "Soy el útero\nde mi propia renacencia.",
  "En mi centro\nhabita la paz eterna.",
  "Soy parte del latido\ncósmico de toda vida.",
  "Mi cuerpo recuerda\nlo que la mente olvidó.",
  "Me rindo a la sabiduría\nancestral de mis células.",
  "Soy tierra fértil.\nTodo lo que necesito ya está.",
  "En el vacío sagrado\nencuentro mi plenitud.",
  "Mi corazón es brújula.\nMi cuerpo, el camino.",
];

function getDecretoDelDia() {
  const start = new Date(new Date().getFullYear(), 0, 0);
  const dayOfYear = Math.floor((Date.now() - start.getTime()) / 86400000);
  return DECRETOS[dayOfYear % DECRETOS.length];
}

/* Sparkle particles radiating from center */
function CoreSparkles() {
  const particles = useMemo(() => Array.from({ length: 15 }, (_, i) => {
    const angle = (i / 15) * Math.PI * 2;
    const dist = 12 + (i % 4) * 5;
    return {
      x: 50 + Math.cos(angle) * dist,
      y: 48 + Math.sin(angle) * dist,
      delay: (i * 0.9) % 12,
      dur: 6 + (i % 4) * 2.5,
      size: 3 + (i % 3) * 1.5,
    };
  }), []);

  return (
    <>
      {particles.map((p, i) => (
        <div key={`sp${i}`} style={{
          position: 'absolute',
          left: `${p.x}%`, top: `${p.y}%`,
          width: `${p.size}px`, height: `${p.size}px`,
          borderRadius: '50%',
          background: '#fffae0',
          boxShadow: `0 0 ${p.size * 2}px ${p.size}px rgba(212,175,55,.4), 0 0 ${p.size * 4}px ${p.size * 2}px rgba(255,240,180,.15)`,
          animation: `sparkleFloat ${p.dur}s ease-in-out infinite`,
          animationDelay: `${p.delay}s`,
        }} />
      ))}
    </>
  );
}

const NucleoGaia = ({ onBack }) => {
  const [enteredAt] = useState(Date.now());
  const [minutes, setMinutes] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [activePractice, setActivePractice] = useState(null);
  const [breathInfo, setBreathInfo] = useState({ label: '', duration: 0, key: 0 });
  const timerRef = useRef(null);

  useEffect(() => {
    setFadeIn(true);
    const interval = setInterval(() => {
      setMinutes(Math.floor((Date.now() - enteredAt) / 60000));
    }, 30000);
    return () => clearInterval(interval);
  }, [enteredAt]);

  /* Breathing phase cycle */
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!activePractice) {
      setBreathInfo({ label: '', duration: 0, key: 0 });
      return;
    }
    const practice = PRACTICES.find(p => p.id === activePractice);
    if (!practice) return;

    let cancelled = false;
    let idx = 0;

    const step = () => {
      if (cancelled) return;
      const phase = practice.phases[idx];
      setBreathInfo(prev => ({ label: phase.label, duration: phase.duration, key: prev.key + 1 }));
      timerRef.current = setTimeout(() => {
        idx = (idx + 1) % practice.phases.length;
        step();
      }, phase.duration);
    };
    step();

    return () => { cancelled = true; clearTimeout(timerRef.current); };
  }, [activePractice]);

  const handleSelect = useCallback((id) => {
    setActivePractice(prev => prev === id ? null : id);
  }, []);

  const handleExit = useCallback(() => {
    setLeaving(true);
    setTimeout(() => onBack(), 1500);
  }, [onBack]);

  const decreto = useMemo(() => getDecretoDelDia(), []);
  const current = PRACTICES.find(p => p.id === activePractice);

  const auraAnim = current
    ? `${current.animName} ${current.animDur} ease-in-out infinite`
    : 'coreBreathIdle 8s ease-in-out infinite';

  return (
    <div className="relative w-full h-screen overflow-hidden" data-testid="nucleo-gaia"
      style={{ background: 'radial-gradient(ellipse at 50% 46%, #faf8f4 0%, #f0ece4 50%, #e8e2d8 100%)' }}>

      <style>{`
        @keyframes coreBreathIdle {
          0%, 100% { transform: scale(1); opacity: .15 }
          50% { transform: scale(1.04); opacity: .35 }
        }
        @keyframes breathEnraizamiento {
          0% { transform: scale(1); opacity: .12 }
          21% { transform: scale(1.09); opacity: .42 }
          58% { transform: scale(1.09); opacity: .42 }
          100% { transform: scale(1); opacity: .12 }
        }
        @keyframes breathCoherencia {
          0%, 100% { transform: scale(1); opacity: .13 }
          50% { transform: scale(1.08); opacity: .40 }
        }
        @keyframes breathOceanica {
          0%, 100% { transform: scale(1); opacity: .10 }
          30% { transform: scale(1.04); opacity: .28 }
          50% { transform: scale(1.07); opacity: .34 }
          70% { transform: scale(1.04); opacity: .28 }
        }
        @keyframes sparkleFloat {
          0%, 100% { opacity: .3; transform: scale(.7) }
          20% { opacity: .9; transform: scale(1.1) }
          50% { opacity: .4; transform: scale(.85) }
          75% { opacity: .85; transform: scale(1.05) }
        }
        @keyframes corePulse {
          0%, 100% { opacity: .12 }
          50% { opacity: .3 }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px) }
          to { opacity: 1; transform: translateY(0) }
        }
        @keyframes decretoReveal {
          from { opacity: 0; transform: translateY(8px); filter: blur(3px) }
          to { opacity: 1; transform: translateY(0); filter: blur(0) }
        }
        @keyframes panelFadeIn {
          from { opacity: 0 }
          to { opacity: 1 }
        }
        @keyframes breathTextIn {
          0% { opacity: 0; letter-spacing: 0.1em }
          12% { opacity: 1; letter-spacing: 0.3em }
          85% { opacity: 1; letter-spacing: 0.3em }
          100% { opacity: 0; letter-spacing: 0.35em }
        }
        @keyframes crystalFloat {
          0%, 100% { transform: translateY(0) }
          50% { transform: translateY(-4px) }
        }
        @keyframes sphereGlowActive {
          0%, 100% { box-shadow: 0 0 60px 30px rgba(212,175,55,.2), 0 0 120px 60px rgba(212,175,55,.08), 0 0 200px 100px rgba(212,175,55,.03) }
          50% { box-shadow: 0 0 80px 40px rgba(212,175,55,.28), 0 0 150px 75px rgba(212,175,55,.12), 0 0 220px 110px rgba(212,175,55,.05) }
        }
      `}</style>

      {/* Entry / Exit cream transition */}
      <div className={`absolute inset-0 z-50 pointer-events-none transition-opacity duration-[1500ms] ${
        !fadeIn || leaving ? 'opacity-100' : 'opacity-0'
      }`} style={{ background: '#faf8f4' }} />

      {/* Crystalline sphere */}
      <img
        src={NUCLEO_IMAGE}
        alt="Núcleo Cristalino de Gaia"
        className="absolute object-cover"
        style={{
          width: '70vh', height: '70vh',
          left: '50%', top: '46%',
          transform: 'translate(-50%,-50%)',
          borderRadius: '50%',
          border: '1px solid rgba(212,175,55,.15)',
          boxShadow: '0 0 60px 30px rgba(212,175,55,.2), 0 0 120px 60px rgba(212,175,55,.08), 0 0 200px 100px rgba(212,175,55,.03)',
          animation: activePractice ? `sphereGlowActive ${current?.animDur || '8s'} ease-in-out infinite` : 'none',
          transition: 'box-shadow 2s ease',
        }}
        draggable={false}
        data-testid="nucleo-bg"
      />

      {/* Effects layer */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        {/* Ambient aura — syncs with breathing */}
        <div style={{
          position: 'absolute', left: '50%', top: '46%',
          transform: 'translate(-50%,-50%)',
          width: '80vh', height: '80vh',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,220,100,.1) 0%, rgba(212,175,55,.03) 40%, transparent 65%)',
          animation: auraAnim,
        }} />
        {/* Outer ring */}
        <div style={{
          position: 'absolute', left: '50%', top: '46%',
          transform: 'translate(-50%,-50%)',
          width: '82vh', height: '82vh',
          borderRadius: '50%',
          border: '1px solid rgba(212,175,55,.06)',
          boxShadow: '0 0 40px 20px rgba(212,175,55,.03), inset 0 0 40px 20px rgba(212,175,55,.02)',
          animation: 'corePulse 10s ease-in-out infinite',
        }} />
        <CoreSparkles />
      </div>

      {/* ========== LEFT PANEL — Decreto del día ========== */}
      <div
        className="absolute left-0 top-0 h-full z-20 flex items-center pointer-events-none"
        style={{ width: '20%', paddingLeft: '5.5%' }}
      >
        <div style={{ animation: 'decretoReveal 2s ease-out 2s both' }}>
          <div className="w-6 h-px mb-4" style={{ background: 'rgba(180,150,80,.25)' }} />
          <p
            className="text-[10px] uppercase tracking-[0.25em] mb-4 font-light"
            style={{ color: 'rgba(140,115,50,.60)' }}
            data-testid="decreto-label"
          >
            Decreto del día
          </p>
          <p
            className="text-sm font-extralight leading-[1.8] whitespace-pre-line"
            style={{ color: 'rgba(90,74,42,.65)', fontStyle: 'italic' }}
            data-testid="decreto-text"
          >
            {decreto}
          </p>
          <div className="w-6 h-px mt-5" style={{ background: 'rgba(180,150,80,.15)' }} />
        </div>
      </div>

      {/* ========== RIGHT PANEL — Crystal practice selectors ========== */}
      <div
        className="absolute right-0 top-0 h-full z-20 flex items-center justify-end"
        style={{ width: '20%', paddingRight: '3.5%' }}
      >
        <div className="flex flex-col items-center" style={{ animation: 'panelFadeIn 1.5s ease-out 2.5s both' }}>
          <p
            className="text-[10px] uppercase tracking-[0.25em] mb-8 font-light"
            style={{ color: 'rgba(140,115,50,.60)' }}
            data-testid="practice-label"
          >
            Meditemos juntas
          </p>

          <div className="flex flex-col items-center gap-7">
            {PRACTICES.map(p => {
              const isActive = activePractice === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => handleSelect(p.id)}
                  className="group flex flex-col items-center focus:outline-none"
                  data-testid={`crystal-${p.id}`}
                >
                  <div
                    className="relative"
                    style={{
                      width: 64, height: 64,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: isActive ? `1px solid ${p.color}` : '1px solid rgba(255,255,255,.06)',
                      boxShadow: isActive
                        ? `0 0 24px 10px ${p.glowColor}, 0 0 50px 20px ${p.glowColor}`
                        : '0 0 12px 4px rgba(212,175,55,.04)',
                      transition: 'all .8s ease',
                      animation: isActive ? 'crystalFloat 3.5s ease-in-out infinite' : 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={p.crystal}
                      alt={p.name}
                      className="w-full h-full object-cover"
                      style={{
                        opacity: isActive ? 1 : 0.45,
                        transition: 'opacity .7s ease',
                        filter: isActive ? 'none' : 'saturate(.5) brightness(.9)',
                      }}
                      draggable={false}
                    />
                  </div>
                  <span
                    className="text-[11px] mt-2.5 font-light tracking-wider transition-all duration-600"
                    style={{ color: isActive ? 'rgba(90,74,42,.85)' : 'rgba(90,74,42,.50)' }}
                  >
                    {p.name}
                  </span>
                  <span
                    className="text-[9px] font-light tracking-widest transition-all duration-600"
                    style={{ color: isActive ? 'rgba(160,130,50,.8)' : 'rgba(160,130,50,.30)' }}
                  >
                    {p.rhythm}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ========== BREATHING GUIDE — below sphere ========== */}
      {activePractice && breathInfo.label && (
        <div
          className="absolute left-1/2 -translate-x-1/2 z-20 pointer-events-none"
          style={{ bottom: '18%' }}
        >
          <p
            key={breathInfo.key}
            className="text-center text-lg font-extralight"
            style={{
              color: current?.color || 'rgba(255,250,240,.4)',
              animation: `breathTextIn ${breathInfo.duration}ms ease-in-out forwards`,
            }}
            data-testid="breath-guide"
          >
            {breathInfo.label}
          </p>
        </div>
      )}

      {/* Time counter */}
      {minutes > 0 && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 pointer-events-none"
          style={{ animation: 'fadeUp .8s ease-out both' }}>
          <p className="text-[#5a4a2a]/25 text-xs tracking-[0.15em] font-light" data-testid="nucleo-time-counter">
            {minutes} {minutes === 1 ? 'minuto' : 'minutos'} en el núcleo
          </p>
        </div>
      )}

      {/* Sacred Space Tab — left side expandable panel */}
      <SacredSpaceTab spaceId="nucleo_gaia" theme="light" />

      {/* Back button — top left, offset for tab */}
      <div className="absolute top-4 left-16 z-40">
        <button
          onClick={handleExit}
          className="flex items-center gap-2 text-[#5a4a2a]/60 hover:text-[#5a4a2a] transition-colors text-sm bg-white/50 backdrop-blur-md rounded-full px-3 py-1.5"
          data-testid="nucleo-exit-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
      </div>
    </div>
  );
};

export default NucleoGaia;
